<template>
  <AppIntersectionObserver class="image-focal" :options="intersectionOptions" @intersect="onIntersect">
    <picture
        class="image-focal__picture"
        :class="pictureClasses"
        :style="`width: 100%; padding-top: ${getAspectRatioHeight};`"
    >

      <AppImageDebugger :renditions="availableSources"/>

      <source
          v-for="(item, index) in availableSources"
          v-if="state.initialized"
          :key="`dynamic-image__${data.image?.id}__${index}`"
          :type="item.rendition.type"
          :media="`(min-width: ${item.min}px) and (max-width: ${item.max}px)`"
          :srcset="item.rendition.srcSet"
      >

      <!-- Removed the 'title' attribute from the image element because the content provided by the CMS
      was often irrelevant or nonsensical. The 'title' attribute was not adding value to the user experience
      and could potentially confuse users. The title attribute is not required for accessibility or SEO,
      the 'alt' attribute is sufficient.

      The 'title' could get the same value as the 'alt' attribute, but this would be redundant
      and not serve the purpose of the 'title' attribute.
      -->
      <img
          :src="state.placeholderSrc"
          :loading="(eager) ? 'eager' : 'lazy'"
          :fetchpriority="(eager) ? 'high' : undefined"
          decoding="async"
          class="image-focal__image"
          :alt="props.data.image?.description || 'AVROTROS'"
      >
      <small v-if="props.data.image?.copyright"
             class="image-focal__small">
        {{ props.data.image?.copyright }}
      </small>
    </picture>
  </AppIntersectionObserver>
</template>

<script setup lang="ts" async>
import {computed, onMounted, reactive} from 'vue'
import {useRuntimeConfig} from 'nuxt/app'
import {useImageFocal} from '~/composables/useImageFocal'
import {TImageFocal} from '~/types/components/image-types'
import {getThumbnail} from '~/services/custom-endpoints'
import {TImageRendition} from '~/types/other-types'
import {useBreakpointsStore} from '~/store/breakpoints'
import {breakpoints} from '~/constants/breakpoints'

const config = useRuntimeConfig()
const breakpointsStore = useBreakpointsStore()
const baseURL = config.public.apiBaseUrl
const intersectionOptions = {
  threshold: '0.1'
}

const props = defineProps<{
  data: TImageFocal,
  focalPoint?: Number,
  renditions: TImageRendition[]
  eager?: Boolean
  pictureClasses?: String // an optional class to style the element.
  orientation?: String
}>()

const availableSources = computed(() => {
  const sources = []

  const reversed = [...breakpoints].reverse()

  reversed.forEach((bp) => {

    const rendition = props.renditions.find(rendition => rendition.breakpoint === bp.name)

    if (rendition) {
      const oneX = useImageFocal(props.data.image, rendition.width, rendition.height, props.data.focalPoints[props.focalPoint || 0])
      const twoX = useImageFocal(props.data.image, rendition.width !== 'auto' ? Math.ceil(rendition.width * 1.5) : 'auto', rendition.height !== 'auto' ? Math.ceil(rendition.height * 1.5) : 'auto', props.data.focalPoints[props.focalPoint || 0], 'webp')

      const prevEntry = sources[sources.length - 1]

      sources.push({
        name: bp.name,
        min: bp.min,
        max: (prevEntry) ? prevEntry.min - 1 : breakpoints[breakpoints.length - 1].max,
        src: `${baseURL}/assets/${twoX.value}`,
        rendition: {
          width: rendition.width,
          height: rendition.height,
          type: 'image/jpeg',
          srcSet: `${baseURL}/assets/${oneX.value} 1x`,
          debugUrl: `${baseURL}/assets/${oneX.value}`
        }
      })

      sources.push({
        name: bp.name,
        min: bp.min,
        max: (prevEntry) ? prevEntry.min - 1 : breakpoints[breakpoints.length - 1].max,
        src: `${baseURL}/assets/${twoX.value}`,
        rendition: {
          width: rendition.width,
          height: rendition.height,
          type: 'image/webp',
          srcSet: `${baseURL}/assets/${twoX.value} 1.5x`,
          debugUrl: null
        }
      })
    }
  })

  return sources.reverse()
})

const getCurrentSrc = () => {
  for (let i = breakpointsStore.getCurrentBreakpointIndex; i >= 0; i--) {
    const foundSource = availableSources.value.find(source => source.name === breakpoints[i].name)
    if (foundSource) {
      return foundSource
    }
  }

  // fallback
  return availableSources.value[0]
}

const getAspectRatioHeight = computed(() => {
  const src = getCurrentSrc()

  if (src.rendition.height === 'auto' || src.rendition.width === 'auto') {
    return 'auto'
  }

  return `${src.rendition.height / src.rendition.width * 100}%`
})

const state = reactive({
  initialized: !!(props.eager),
  sources: (props.eager) ? availableSources.value : [],
  currentSrc: getCurrentSrc(),
  placeholderSrc: undefined
})

// focal point is always an array, so we use the first entry
const ratio = availableSources.value[0].rendition.height / availableSources.value[0].rendition.width
const placeholderPath = useImageFocal(props.data.image, 10, Math.round(10 * ratio), props.data.focalPoints[props.focalPoint || 0], 'gif')

// only return a thumbnail when rendering serverside
const smallestSource = availableSources.value[1]
// if (process.client) {
const {data: thumbData, error} = await getThumbnail(placeholderPath.value)
if (thumbData.value) {
  state.placeholderSrc = thumbData.value
} else {
  state.placeholderSrc = (smallestSource) ? smallestSource.src : '#'
}
// } else {
//   state.placeholderSrc = (smallestSource) ? smallestSource.src : '#'
// }

const onIntersect = (e) => {
  if (e.visible === true && !state.initialized) {
    state.initialized = true
    state.sources = availableSources.value
    state.currentSrc = getCurrentSrc()
  }
}

// // update the image size based on the current breakpoint
onMounted(() => {
  breakpointsStore.$subscribe(() => {
    state.currentSrc = getCurrentSrc()
  })
})

</script>

<style scoped lang="postcss">
.image-focal {
  &__picture {
    position: relative;
    display: block;
    overflow: clip;

    &--full-height {
      height: 100%;
    }

    &--contain-landscape {
      aspect-ratio: 16/9 !important;
      max-height: 612px;
      background-color: rgba(0, 0, 0, 0.25);

      & .image-focal__image {
        object-fit: contain !important;
      }
    }

    &--contain-portrait {
      aspect-ratio: 4/5 !important;
      max-height: 800px;
      background-color: rgba(0, 0, 0, 0.25);

      & .image-focal__image {
        object-fit: contain !important;
      }
    }
  }

  &__image {
    position: absolute;
    left: 0px;
    top: 0px;
    max-height: 100%;
    min-height: 100%;
    width: 100%;
    object-fit: cover;
  }

  &__small {
    position: absolute;
    bottom: 0px;
    right: 0px;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    text-align: right;
    font-size: 9px;
    text-transform: uppercase;
    color: #fff;
    opacity: 0.5;
    mix-blend-mode: difference;
  }
}
</style>
